<script setup lang="ts">
import type { TraderRisk, TraderRiskItem } from '~/server/detail'

const { risk } = defineProps<{
  risk?: TraderRisk | null
}>()

const emit = defineEmits<{
  open: []
}>()

const { t } = useAppI18n()
const riskItems = risk?.items || []

const riskHigh = riskItems.filter((riskItem) => riskItem.level === 4)
const riskMedium = riskItems.filter((riskItem) => riskItem.level === 2 || riskItem.level === 3)

const cardStyle = computed(() => {
  let gradient = 'linear-gradient(180deg, #ffedee 0%, #fff 80%)'
  let numberColor = '#e2483d'
  if (riskHigh.length === 0 && riskMedium.length > 0) {
    gradient = 'linear-gradient(180deg, #FFF5E0 0%, #FFF 80%)'
    numberColor = '#F68909'
  }

  return {
    '--risk-card-border': risk?.borderColor,
    '--risk-card-tint': risk?.backgroundColor,
    '--risk-card-bg-gradient': gradient,
    '--risk-card-number-color': numberColor,
  }
})

// const getRiskLevel = (risk: TraderRiskItem) => {
//   if (risk.level === 1) return 'high'
//   if (risk.level === 2) return 'medium'
//   return 'low'
// }
</script>

<template>
  <section
    class="risk-card"
    :style="cardStyle"
    role="button"
    tabindex="0"
    aria-label="查看风险评测"
    @click="emit('open')"
    @keydown.enter.prevent="emit('open')"
    @keydown.space.prevent="emit('open')"
  >
    <h2 class="card-title">
      <span class="danger-text">{{ t('83875') }}</span
      >{{ t('83876') }}
      <!-- <span class="info-icon">i</span> -->
    </h2>

    <div class="risk-count">
      <span class="risk-number">{{ risk?.count }}</span>
      <span>{{ t('83877') }}</span>
    </div>

    <div class="risk-list" v-if="riskHigh.length && riskMedium.length">
      <div class="risk-pill risk-pill-high">
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/06/07/a0be69ee-6f58-47f4-8a00-96a37300c0c7.png"
          alt=""
          class="h-[16px] w-[16px]"
          srcset=""
          loading="eager"
        />

        {{ t('83878') + '&nbsp;' + riskHigh?.length }}
      </div>
      <div class="risk-pill risk-pill-medium">
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/06/07/f2116b60-df3e-4ad6-b07d-787de54c7620.png"
          alt=""
          class="h-[16px] w-[16px]"
          srcset=""
          loading="eager"
        />
        {{ t('83879') + '&nbsp;' + riskMedium.length }}
      </div>
    </div>
    <div v-if="riskHigh.length && !riskMedium.length">
      <div v-if="riskHigh.length === 1">
        <div class="h-[0.5px] w-full bg-[rgba(5,21,36,0.06)]"></div>
        <div class="text-medium mt-[8px] line-clamp-3 text-[14px] leading-[1.2] break-words text-[#505258]">
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/06/07/a0be69ee-6f58-47f4-8a00-96a37300c0c7.png"
            alt=""
            class="mr-[4px] inline-block h-[16px] w-[16px] align-[-3px]"
            srcset=""
            loading="eager"
          />
          {{ riskHigh[0]?.content }}
        </div>
      </div>
      <div
        v-else
        class="mt-[28px] flex h-[48px] w-full items-center gap-[4px] rounded-[100px] border-[0.5px] border-solid border-[rgba(5,21,36,0.06)] bg-[rgba(238,209,43,0.10)] px-[12px] py-[4px] text-[14px] text-[#E2483D]"
      >
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/06/07/a0be69ee-6f58-47f4-8a00-96a37300c0c7.png"
          alt=""
          class="h-[16px] w-[16px]"
          srcset=""
          loading="eager"
        />
        {{ t('83878') + riskHigh.length }}
      </div>
    </div>

    <div v-if="!riskHigh.length && riskMedium.length">
      <div class="h-[0.5px] w-full bg-[rgba(5,21,36,0.06)]"></div>
      <div v-if="riskMedium.length === 1">
        <div class="text-medium mt-[8px] line-clamp-3 text-[14px] leading-[1.2] break-words text-[#505258]">
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/06/07/f2116b60-df3e-4ad6-b07d-787de54c7620.png"
            alt=""
            class="mr-[4px] inline-block h-[16px] w-[16px] align-[-3px]"
            srcset=""
            loading="eager"
          />
          {{ riskMedium[0]?.content }}
        </div>
      </div>
      <div
        v-else
        class="mt-[28px] flex h-[48px] w-full items-center gap-[4px] rounded-[100px] border-[0.5px] border-solid border-[rgba(5,21,36,0.06)] bg-[rgba(238,209,43,0.10)] px-[12px] py-[4px] text-[14px] text-[#F68909]"
      >
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/06/07/f2116b60-df3e-4ad6-b07d-787de54c7620.png"
          alt=""
          class="h-[16px] w-[16px]"
          srcset=""
          loading="eager"
        />
        {{ t('83879') + riskMedium.length }}
      </div>
    </div>
  </section>
</template>

<style scoped lang="less">
.risk-card {
  position: relative;
  width: 100%;
  height: 212px;
  overflow: hidden;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  cursor: pointer;
  padding: 20px;
  background:
    url(https://resources1.interface003.com/web30/2026/06/05/7f0412e2-2357-4f8d-9a5e-290130c91731.svg) no-repeat right
      bottom,
    var(--risk-card-bg-gradient);
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.risk-card:hover {
  box-shadow: 0 12px 32px rgba(8, 19, 62, 0.08);
}

.risk-card:focus-visible {
  outline: 2px solid rgba(232, 72, 67, 0.28);
  outline-offset: 2px;
}

.risk-card > * {
  position: relative;
  z-index: 1;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  color: #292a2e;
  font-size: 18px;
  font-weight: 600;
}

.danger-text {
  color: var(--risk-card-number-color);
}

.info-icon {
  display: inline-flex;
  width: 14px;
  height: 14px;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #8b929e;
  border-radius: 50%;
  color: #8b929e;
  font-family: Arial, sans-serif;
  font-size: 10px;
  font-weight: 700;
}

.risk-count {
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
}

.risk-number {
  color: var(--risk-card-number-color);
  font-family: 'WikiFX-bold';
  font-size: 40px;
  font-weight: 700;
}

.risk-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
}

.risk-pill {
  display: flex;
  height: 32px;
  align-items: center;
  gap: 4px;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 0 16px;
  font-family:
    PingFang SC,
    sans-serif;
  font-size: 14px;
  font-weight: 700;
}

.risk-pill-high {
  border-color: rgba(232, 72, 67, 0.12);
  background: rgba(232, 72, 67, 0.08);
  color: #e44843;
}

.risk-pill-medium {
  border-color: rgba(255, 142, 0, 0.14);
  background: rgba(255, 196, 67, 0.14);
  color: #ff8e00;
}

.risk-pill-low {
  border-color: rgba(17, 45, 115, 0.12);
  background: rgba(17, 45, 115, 0.08);
  color: #112d73;
}

.risk-dot {
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: 11px;
  font-weight: 700;
}

.risk-pill-high .risk-dot {
  background: #e44843;
}

.risk-pill-medium .risk-dot {
  background: #ff8e00;
}

.risk-pill-low .risk-dot {
  background: #112d73;
}

.risk-image {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  object-fit: contain;
}
</style>
